import { ModuleImportError } from "@/util/custom-errors.mjs";

/**
 * Convenience function to wrap a dynamically imported module's default
 * export function. The import must be passed in as `() => import("...")`
 * so that static analysis works.
 *
 * The reason why one would want to load a module as a separate entry
 * point is to avoid bundling all of that modules dependencies in the
 * main entry point of the app. This results in smaller initial chunk
 * sizes and improves app startup performance by loading less code
 * to start with.
 */
export default function lazyFn<Fn extends ImportFn>(importFn: Fn): LazyFn<Fn> {
  return async function (...args: unknown[]) {
    let fn;

    try {
      fn = (await importFn()).default;
    } catch (err) {
      throw new ModuleImportError(err["message"]);
    }

    return fn(...args);
  } as LazyFn<Fn>;
}

type ImportFn = () => Promise<{ default: (...args: unknown[]) => unknown }>;

type LazyFn<Fn extends () => unknown> = Fn extends () => Promise<{
  default: (...args: infer Args) => infer Return;
}>
  ? (...args: Args) => Promise<Awaited<Return>>
  : never;
